import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { IoClose, IoSearchOutline, IoAddCircleOutline, IoTrashOutline, IoLinkOutline } from "react-icons/io5";
import "./Updates.css";

const API_BASE_URL = process.env.REACT_APP_API_BASEURL;

// NOTE: Adjust these two base paths if your router is mounted differently.
// Admin (protected) routes -> createUpdate / updateUpdate / deleteUpdate / increaseUpdateView
const ADMIN_BASE = `${API_BASE_URL}/admin`;
// Common (public) routes -> getAllUpdates / getUpdateById
const COMMON_BASE = `${API_BASE_URL}`;

const CATEGORIES = [
  "IGNOU",
  "Government Vacancy",
  "Private Jobs",
  "Admit Card",
  "Result",
  "Answer Key",
  "Admission",
  "Scholarship",
  "Counselling",
  "Exam",
  "Syllabus",
  "Exam Pattern",
  "Cut Off",
  "Merit List",
  "University",
  "College",
  "Recruitment",
  "Internship",
  "Internship Result",
  "Interview Schedule",
  "Document Verification",
  "Seat Allotment",
  "Important Notice",
  "Notification",
  "Latest Update",
  "Career News",
  "Other",
];

const STATUSES = ["Draft", "Published"];

const EMPTY_FORM = {
  category: "",
  title: "",
  shortDescription: "",
  description: "",
  tagsInput: "",
  links: [],
  important: false,
  featured: false,
  status: "Published",
  expiresAt: "",
};

const toLocalInputValue = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const formatReadableDate = (isoString) => {
  if (!isoString) return "--";
  return new Date(isoString).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Updates = () => {
  const [updates, setUpdates] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [importantFilter, setImportantFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const authHeaders = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (importantFilter !== "all") params.important = importantFilter;
      if (featuredFilter !== "all") params.featured = featuredFilter;

      const response = await axios.get(`${COMMON_BASE}/update`, { params });
      setUpdates(response.data?.data || []);
      setTotal(response.data?.total || 0);
      setTotalPages(response.data?.totalPages || 1);
      setPageError("");
    } catch (error) {
      console.log(error);
      setPageError("Couldn't load updates. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, categoryFilter, statusFilter, importantFilter, featuredFilter]);

  // Debounce search so we don't fire a request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchUpdates();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const activeFilterCount = useMemo(() => {
    return [categoryFilter, statusFilter, importantFilter, featuredFilter].filter(
      (v) => v !== "all"
    ).length;
  }, [categoryFilter, statusFilter, importantFilter, featuredFilter]);

  const clearFilters = () => {
    setCategoryFilter("all");
    setStatusFilter("all");
    setImportantFilter("all");
    setFeaturedFilter("all");
    setSearchTerm("");
    setPage(1);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (update) => {
    setEditingId(update._id);
    setFormData({
      category: update.category || "",
      title: update.title || "",
      shortDescription: update.shortDescription || "",
      description: update.description || "",
      tagsInput: (update.tags || []).join(", "),
      links: update.links?.length ? update.links.map((l) => ({ ...l })) : [],
      important: !!update.important,
      featured: !!update.featured,
      status: update.status || "Published",
      expiresAt: toLocalInputValue(update.expiresAt),
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setIsModalOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLinkChange = (index, field, value) => {
    setFormData((prev) => {
      const links = [...prev.links];
      links[index] = { ...links[index], [field]: value };
      return { ...prev, links };
    });
  };

  const addLinkRow = () => {
    setFormData((prev) => ({
      ...prev,
      links: [...prev.links, { title: "", url: "" }],
    }));
  };

  const removeLinkRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.category) errors.category = "Choose a category.";
    if (!formData.title.trim()) errors.title = "Title is required.";
    if (!formData.shortDescription.trim())
      errors.shortDescription = "Short description is required.";
    else if (formData.shortDescription.length > 250)
      errors.shortDescription = "Keep it under 250 characters.";
    if (!formData.description.trim())
      errors.description = "Description is required.";

    formData.links.forEach((link, idx) => {
      if ((link.title && !link.url) || (!link.title && link.url)) {
        errors.links = `Link #${idx + 1} needs both a title and a URL.`;
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      category: formData.category,
      title: formData.title.trim(),
      shortDescription: formData.shortDescription.trim(),
      description: formData.description.trim(),
      tags: formData.tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      links: formData.links.filter((l) => l.title?.trim() && l.url?.trim()),
      important: formData.important,
      featured: formData.featured,
      status: formData.status,
      expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null,
    };

    try {
      setIsSaving(true);
      if (editingId) {
        await axios.put(`${ADMIN_BASE}/${editingId}`, payload, authHeaders);
      } else {
        await axios.post(`${ADMIN_BASE}/update`, payload, authHeaders);
      }
      setIsModalOpen(false);
      fetchUpdates();
    } catch (error) {
      console.log(error);
      setFormErrors({
        submit:
          error?.response?.data?.message ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Do you want to delete this update? This cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${ADMIN_BASE}/${id}`, authHeaders);
      setUpdates((prev) => prev.filter((u) => u._id !== id));
    } catch (error) {
      console.log(error);
      alert("Failed to delete update.");
    }
  };

  return (
    <div id="add-subject-inner-wrapper">
      <div className="superheader">
        <div className="subjectHeaderdiv">
          <h5>Updates</h5>
          <div className="subjectdiv2">
            <button className="handleNewSubject" onClick={openCreateModal}>
              + Add New Update
            </button>
          </div>
        </div>

        <div className="form-sub-container mt-4" id="form">
          <div className="upd-toolbar">
            <div className="upd-search">
              <IoSearchOutline className="upd-search-icon" />
              <input
                type="text"
                placeholder="Search by title, description or tag"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <select
              className="upd-filter-select"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All categories</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              className="upd-filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select
              className="upd-filter-select"
              value={importantFilter}
              onChange={(e) => {
                setImportantFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">Important: any</option>
              <option value="true">Important only</option>
              <option value="false">Not important</option>
            </select>

            <select
              className="upd-filter-select"
              value={featuredFilter}
              onChange={(e) => {
                setFeaturedFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">Featured: any</option>
              <option value="true">Featured only</option>
              <option value="false">Not featured</option>
            </select>

            {activeFilterCount > 0 && (
              <button className="upd-clear-btn" onClick={clearFilters}>
                Clear filters ({activeFilterCount})
              </button>
            )}
          </div>

          <h4 className="subject-detail">
            Updates <span className="upd-count">({total})</span>
          </h4>

          {pageError && <div className="upd-alert">{pageError}</div>}

          <div className="upd-table-wrap">
            <table className="classes-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Category</th>
                  <th>Title</th>
                  <th>Flags</th>
                  <th>Status</th>
                  <th>Published</th>
                  <th>Views</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="upd-empty-row">
                      Loading updates...
                    </td>
                  </tr>
                ) : updates.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="upd-empty-row">
                      No updates match your search. Try adjusting the filters
                      or add a new update.
                    </td>
                  </tr>
                ) : (
                  updates.map((update, idx) => (
                    <tr key={update._id}>
                      <td>{(page - 1) * limit + idx + 1}</td>
                      <td>
                        <span className="upd-category-pill">{update.category}</span>
                      </td>
                      <td>
                        <div className="upd-title-cell">{update.title}</div>
                        <div className="upd-short-desc">
                          {update.shortDescription?.slice(0, 60)}
                          {update.shortDescription?.length > 60 ? "..." : ""}
                        </div>
                      </td>
                      <td>
                        <div className="upd-flags">
                          {update.important && (
                            <span className="upd-flag upd-flag-important">Important</span>
                          )}
                          {update.featured && (
                            <span className="upd-flag upd-flag-featured">Featured</span>
                          )}
                          {!update.important && !update.featured && "--"}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`upd-status-badge upd-status-${update.status?.toLowerCase()}`}
                        >
                          {update.status}
                        </span>
                      </td>
                      <td>{formatReadableDate(update.publishedAt)}</td>
                      <td>{update.views ?? 0}</td>
                      <td className="table-action-icons">
                        <i
                          className="fa-solid fa-file-pen"
                          title="Edit update"
                          onClick={() => openEditModal(update)}
                        ></i>
                        <i
                          className="fa-solid fa-trash-can"
                          title="Delete update"
                          onClick={() => handleDelete(update._id)}
                        ></i>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="upd-pagination">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="upd-modal-overlay" onClick={closeModal}>
          <div className="upd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upd-modal-header">
              <h4>{editingId ? "Edit Update" : "Add New Update"}</h4>
              <button className="upd-modal-close" onClick={closeModal} aria-label="Close">
                <IoClose />
              </button>
            </div>

            <form className="upd-form" onSubmit={handleSubmit}>
              {formErrors.submit && <div className="upd-alert">{formErrors.submit}</div>}

              <div className="upd-form-grid">
                <div className="upd-field">
                  <label>Category *</label>
                  <select name="category" value={formData.category} onChange={handleFormChange}>
                    <option value="">Select a category</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  {formErrors.category && (
                    <span className="upd-field-error">{formErrors.category}</span>
                  )}
                </div>

                <div className="upd-field">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleFormChange}>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="upd-field upd-field-full">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="e.g. IGNOU June 2026 Admit Card Released"
                  />
                  {formErrors.title && (
                    <span className="upd-field-error">{formErrors.title}</span>
                  )}
                </div>

                <div className="upd-field upd-field-full">
                  <label>
                    Short Description *
                    <span className="upd-char-count">
                      {formData.shortDescription.length}/250
                    </span>
                  </label>
                  <textarea
                    name="shortDescription"
                    value={formData.shortDescription}
                    onChange={handleFormChange}
                    rows={2}
                    maxLength={250}
                    placeholder="One or two lines shown on listing cards"
                  />
                  {formErrors.shortDescription && (
                    <span className="upd-field-error">{formErrors.shortDescription}</span>
                  )}
                </div>

                <div className="upd-field upd-field-full">
                  <label>Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    rows={5}
                    placeholder="Full details of this update"
                  />
                  {formErrors.description && (
                    <span className="upd-field-error">{formErrors.description}</span>
                  )}
                </div>

                <div className="upd-field upd-field-full">
                  <label>Tags</label>
                  <input
                    type="text"
                    name="tagsInput"
                    value={formData.tagsInput}
                    onChange={handleFormChange}
                    placeholder="Comma separated, e.g. ignou, admit-card, 2026"
                  />
                </div>

                <div className="upd-field upd-field-full">
                  <label>Links</label>
                  <div className="upd-links-list">
                    {formData.links.map((link, idx) => (
                      <div className="upd-link-row" key={idx}>
                        <IoLinkOutline className="upd-link-icon" />
                        <input
                          type="text"
                          placeholder="Link title (e.g. Download PDF)"
                          value={link.title}
                          onChange={(e) => handleLinkChange(idx, "title", e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="https://..."
                          value={link.url}
                          onChange={(e) => handleLinkChange(idx, "url", e.target.value)}
                        />
                        <button
                          type="button"
                          className="upd-link-remove"
                          onClick={() => removeLinkRow(idx)}
                          aria-label="Remove link"
                        >
                          <IoTrashOutline />
                        </button>
                      </div>
                    ))}
                    <button type="button" className="upd-add-link-btn" onClick={addLinkRow}>
                      <IoAddCircleOutline /> Add a link
                    </button>
                  </div>
                  {formErrors.links && (
                    <span className="upd-field-error">{formErrors.links}</span>
                  )}
                </div>

                <div className="upd-field">
                  <label>Expires On</label>
                  <input
                    type="datetime-local"
                    name="expiresAt"
                    value={formData.expiresAt}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="upd-field upd-checkbox-field">
                  <label className="upd-checkbox-label">
                    <input
                      type="checkbox"
                      name="important"
                      checked={formData.important}
                      onChange={handleFormChange}
                    />
                    Mark as important
                  </label>
                  <label className="upd-checkbox-label">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleFormChange}
                    />
                    Mark as featured
                  </label>
                </div>
              </div>

              <div className="upd-modal-actions">
                <button
                  type="button"
                  className="upd-btn-secondary"
                  onClick={closeModal}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="upd-btn-primary" disabled={isSaving}>
                  {isSaving ? "Saving..." : editingId ? "Save Changes" : "Create Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Updates;